import { useState, useEffect, useRef } from 'react';
import { TextField, TextFieldProps } from '@mui/material';

type TVTextFieldProps = TextFieldProps & {
  id?: string;
  max?: number;
  valor?: (id: string, valor: string) => void;
};

const ALLOWED_KEYS = ['Tab', 'Enter', 'Escape', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];

const formatarPercentual = (digitos: string): string => {
  const num = Number(digitos || '0') / 100;
  return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Mesmo modelo "calculadora" do campo monetário: dígitos como única fonte de
// verdade, sem re-derivar do texto formatado — evita o travamento ao apagar.
export const VFieldPercentFaturamento: React.FC<TVTextFieldProps> = ({
  valor,
  id,
  max = 90,
  ...rest
}) => {
  const [digitos, setDigitos] = useState('');
  const digitandoRef = useRef(false);

  useEffect(() => {
    if (digitandoRef.current) { digitandoRef.current = false; return; }
    if (rest.value === undefined || rest.value === null || rest.value === '') { setDigitos(''); return; }
    const centesimos = Math.round(Math.abs(Number(rest.value)) * 100);
    setDigitos(centesimos > 0 ? String(centesimos) : '');
  }, [rest.value]);

  const commit = (novosDigitos: string) => {
    let limpos = novosDigitos.replace(/^0+/, '').slice(0, 8);
    const numero = Number(limpos || '0') / 100;
    if (numero > max) limpos = String(Math.round(max * 100));
    setDigitos(limpos);
    digitandoRef.current = true;
    valor?.(id || '', (Number(limpos || '0') / 100).toFixed(2));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey || e.altKey) { rest.onKeyDown?.(e); return; }
    if (/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      commit(digitos + e.key);
    } else if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      commit(digitos.slice(0, -1));
    } else if (!ALLOWED_KEYS.includes(e.key)) {
      e.preventDefault();
    }
    rest.onKeyDown?.(e);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const apenasDigitos = e.clipboardData.getData('text').replace(/\D/g, '');
    if (apenasDigitos) commit(digitos + apenasDigitos);
  };

  return (
    <TextField
      {...rest}
      value={formatarPercentual(digitos)}
      onChange={() => {}}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      inputProps={{ inputMode: 'numeric', ...(rest.inputProps || {}) }}
    />
  );
};
